// contracts/contractsStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
    contractsService,
    Contract,
    ContractTemplate,
    ContractStats,
    CreateContractData,
    UpdateContractData,
    ContractFlag
} from '../services/contracts.service';

interface ContractsState {
    // State
    contracts: Contract[];
    templates: ContractTemplate[];
    stats: ContractStats | null;
    isLoading: boolean;
    error: string | null;

    // Filters
    searchTerm: string;
    filterStatus: string;
    filterTab: 'active' | 'pending' | 'signed' | 'templates' | 'all';
    sortBy: 'title' | 'value' | 'status' | 'endDate' | 'updatedAt';
    sortOrder: 'asc' | 'desc';

    // Actions
    setSearchTerm: (term: string) => void;
    setFilterStatus: (status: string) => void;
    setFilterTab: (tab: ContractsState['filterTab']) => void;
    setSortBy: (sort: ContractsState['sortBy']) => void;
    setSortOrder: (order: ContractsState['sortOrder']) => void;
    clearError: () => void;

    // Async Actions - Contracts
    fetchContracts: () => Promise<void>;
    fetchStats: () => Promise<void>;
    createContract: (data: CreateContractData) => Promise<Contract | null>;
    createFromTemplate: (templateId: string, data: {
        partnershipId: string;
        title: string;
        totalValue: number;
        startDate: string;
        endDate: string;
    }) => Promise<Contract | null>;
    updateContract: (id: string, data: UpdateContractData) => Promise<Contract | null>;
    updateContractStatus: (id: string, status: Contract['status']) => Promise<Contract | null>;
    deleteContract: (id: string) => Promise<boolean>;

    // Async Actions - Signatures
    signByCreator: (id: string, signature?: string) => Promise<Contract | null>;
    signByBrand: (id: string, signature?: string) => Promise<Contract | null>;

    // Async Actions - Deliverables
    addDeliverable: (id: string, deliverable: { title: string; description?: string; deadline?: string }) => Promise<Contract | null>;
    updateDeliverable: (id: string, deliverableId: string, data: { status?: string }) => Promise<Contract | null>;

    // Async Actions - Amendments & Flags
    addAmendment: (id: string, changes: string) => Promise<Contract | null>;
    addFlag: (id: string, flag: { type: ContractFlag['type']; description: string }) => Promise<Contract | null>;
    resolveFlag: (id: string, flagId: string, resolution: string) => Promise<Contract | null>;

    // Async Actions - Invoice Generation
    generateInvoiceForMilestone: (contractId: string, milestoneId: string, dueDate?: Date) => Promise<{ invoice: any; contract: Contract } | null>;
    generateFullContractInvoice: (contractId: string, dueDate?: Date) => Promise<{ invoice: any; contract: Contract } | null>;

    // Async Actions - Templates
    fetchTemplates: () => Promise<void>;
    createTemplate: (data: Partial<ContractTemplate>) => Promise<ContractTemplate | null>;
    updateTemplate: (id: string, data: Partial<ContractTemplate>) => Promise<ContractTemplate | null>;
    deleteTemplate: (id: string) => Promise<boolean>;

    // Computed getters
    getFilteredContracts: () => Contract[];
    getActiveContracts: () => Contract[];
    getPendingSignatureContracts: () => Contract[];
    getSignedContracts: () => Contract[];
    getContractsByStatus: () => Record<string, Contract[]>;
    getTotalValue: () => number;
    getCompletedValue: () => number;
}

export const useContractsStore = create<ContractsState>()(
    devtools(
        persist(
            (set, get) => ({
                // Initial state
                contracts: [],
                templates: [],
                stats: null,
                isLoading: false,
                error: null,
                searchTerm: '',
                filterStatus: 'all',
                filterTab: 'all',
                sortBy: 'updatedAt',
                sortOrder: 'desc',

                // Filter actions
                setSearchTerm: (term) => set({ searchTerm: term }),
                setFilterStatus: (status) => set({ filterStatus: status }),
                setFilterTab: (tab) => set({ filterTab: tab }),
                setSortBy: (sort) => set({ sortBy: sort }),
                setSortOrder: (order) => set({ sortOrder: order }),
                clearError: () => set({ error: null }),

                // Fetch contracts
                fetchContracts: async () => {
                    set({ isLoading: true, error: null });
                    try {
                        const contracts = await contractsService.getAll();
                        set({ contracts, isLoading: false, error: null });
                    } catch (error) {
                        set({
                            isLoading: false,
                            error: error instanceof Error ? error.message : 'Failed to fetch contracts'
                        });
                    }
                },

                // Fetch stats
                fetchStats: async () => {
                    try {
                        const stats = await contractsService.getStats();
                        set({ stats });
                    } catch (error) {
                        console.error('Failed to fetch stats:', error);
                    }
                },

                // Create contract
                createContract: async (data) => {
                    set({ isLoading: true, error: null });
                    try {
                        const newContract = await contractsService.create(data);
                        set((state) => ({
                            contracts: [newContract, ...state.contracts],
                            isLoading: false,
                            error: null
                        }));
                        return newContract;
                    } catch (error) {
                        set({
                            isLoading: false,
                            error: error instanceof Error ? error.message : 'Failed to create contract'
                        });
                        return null;
                    }
                },

                // Create from template
                createFromTemplate: async (templateId, data) => {
                    set({ isLoading: true, error: null });
                    try {
                        const newContract = await contractsService.createFromTemplate(templateId, data);
                        set((state) => ({
                            contracts: [newContract, ...state.contracts],
                            isLoading: false,
                            error: null
                        }));
                        return newContract;
                    } catch (error) {
                        set({
                            isLoading: false,
                            error: error instanceof Error ? error.message : 'Failed to create contract from template'
                        });
                        return null;
                    }
                },

                // Update contract
                updateContract: async (id, data) => {
                    set({ isLoading: true, error: null });
                    try {
                        const updatedContract = await contractsService.update(id, data);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === id ? updatedContract : c),
                            isLoading: false,
                            error: null
                        }));
                        return updatedContract;
                    } catch (error) {
                        set({
                            isLoading: false,
                            error: error instanceof Error ? error.message : 'Failed to update contract'
                        });
                        return null;
                    }
                },

                // Update contract status
                updateContractStatus: async (id, status) => {
                    set({ isLoading: true, error: null });
                    try {
                        const updatedContract = await contractsService.updateStatus(id, status);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === id ? updatedContract : c),
                            isLoading: false,
                            error: null
                        }));
                        return updatedContract;
                    } catch (error) {
                        set({
                            isLoading: false,
                            error: error instanceof Error ? error.message : 'Failed to update contract status'
                        });
                        return null;
                    }
                },

                // Delete contract
                deleteContract: async (id) => {
                    set({ isLoading: true, error: null });
                    try {
                        await contractsService.delete(id);
                        set((state) => ({
                            contracts: state.contracts.filter(c => c.id !== id),
                            isLoading: false,
                            error: null
                        }));
                        return true;
                    } catch (error) {
                        set({
                            isLoading: false,
                            error: error instanceof Error ? error.message : 'Failed to delete contract'
                        });
                        return false;
                    }
                },

                // Sign by creator
                signByCreator: async (id, signature) => {
                    try {
                        const updatedContract = await contractsService.signByCreator(id, signature);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === id ? updatedContract : c)
                        }));
                        return updatedContract;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to sign contract' });
                        return null;
                    }
                },

                // Sign by brand
                signByBrand: async (id, signature) => {
                    try {
                        const updatedContract = await contractsService.signByBrand(id, signature);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === id ? updatedContract : c)
                        }));
                        return updatedContract;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to sign contract' });
                        return null;
                    }
                },

                // Add deliverable
                addDeliverable: async (id, deliverable) => {
                    try {
                        const updatedContract = await contractsService.addDeliverable(id, deliverable);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === id ? updatedContract : c)
                        }));
                        return updatedContract;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to add deliverable' });
                        return null;
                    }
                },

                // Update deliverable
                updateDeliverable: async (id, deliverableId, data) => {
                    try {
                        const updatedContract = await contractsService.updateDeliverable(id, deliverableId, data);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === id ? updatedContract : c)
                        }));
                        return updatedContract;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to update deliverable' });
                        return null;
                    }
                },

                // Add amendment
                addAmendment: async (id, changes) => {
                    try {
                        const updatedContract = await contractsService.addAmendment(id, changes);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === id ? updatedContract : c)
                        }));
                        return updatedContract;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to add amendment' });
                        return null;
                    }
                },

                // Add flag
                addFlag: async (id, flag) => {
                    try {
                        const updatedContract = await contractsService.addFlag(id, flag);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === id ? updatedContract : c)
                        }));
                        return updatedContract;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to add flag' });
                        return null;
                    }
                },

                // Resolve flag
                resolveFlag: async (id, flagId, resolution) => {
                    try {
                        const updatedContract = await contractsService.resolveFlag(id, flagId, resolution);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === id ? updatedContract : c)
                        }));
                        return updatedContract;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to resolve flag' });
                        return null;
                    }
                },

                // Generate invoice for milestone
                generateInvoiceForMilestone: async (contractId, milestoneId, dueDate) => {
                    try {
                        const result = await contractsService.generateInvoiceForMilestone(contractId, milestoneId, dueDate);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === contractId ? result.contract : c)
                        }));
                        return result;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to generate invoice' });
                        return null;
                    }
                },

                // Generate full contract invoice
                generateFullContractInvoice: async (contractId, dueDate) => {
                    try {
                        const result = await contractsService.generateFullContractInvoice(contractId, dueDate);
                        set((state) => ({
                            contracts: state.contracts.map(c => c.id === contractId ? result.contract : c)
                        }));
                        return result;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to generate invoice' });
                        return null;
                    }
                },

                // Fetch templates
                fetchTemplates: async () => {
                    try {
                        const templates = await contractsService.getTemplates();
                        set({ templates });
                    } catch (error) {
                        console.error('Failed to fetch templates:', error);
                    }
                },

                // Create template
                createTemplate: async (data) => {
                    try {
                        const newTemplate = await contractsService.createTemplate(data);
                        set((state) => ({
                            templates: [...state.templates, newTemplate]
                        }));
                        return newTemplate;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to create template' });
                        return null;
                    }
                },

                // Update template
                updateTemplate: async (id, data) => {
                    try {
                        const updatedTemplate = await contractsService.updateTemplate(id, data);
                        set((state) => ({
                            templates: state.templates.map(t => t.id === id ? updatedTemplate : t)
                        }));
                        return updatedTemplate;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to update template' });
                        return null;
                    }
                },

                // Delete template
                deleteTemplate: async (id) => {
                    try {
                        await contractsService.deleteTemplate(id);
                        set((state) => ({
                            templates: state.templates.filter(t => t.id !== id)
                        }));
                        return true;
                    } catch (error) {
                        set({ error: error instanceof Error ? error.message : 'Failed to delete template' });
                        return false;
                    }
                },

                // Computed getters
                getFilteredContracts: () => {
                    const { contracts, searchTerm, filterStatus, filterTab, sortBy, sortOrder } = get();

                    let filtered = contracts.filter(contract => {
                        // Search filter
                        const matchesSearch = !searchTerm ||
                            contract.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (contract.partnership?.brand || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (contract.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

                        // Status filter
                        const matchesStatus = filterStatus === 'all' || contract.status === filterStatus;

                        // Tab filter
                        let matchesTab = true;
                        if (filterTab === 'active') {
                            matchesTab = contract.status === 'active';
                        } else if (filterTab === 'pending') {
                            matchesTab = ['draft', 'sent', 'negotiation'].includes(contract.status);
                        } else if (filterTab === 'signed') {
                            matchesTab = ['signed', 'active', 'completed'].includes(contract.status);
                        }

                        return matchesSearch && matchesStatus && matchesTab;
                    });

                    // Sort results
                    filtered.sort((a, b) => {
                        let aVal: any;
                        let bVal: any;

                        switch (sortBy) {
                            case 'value':
                                aVal = a.totalValue;
                                bVal = b.totalValue;
                                break;
                            case 'endDate':
                                aVal = new Date(a.endDate).getTime();
                                bVal = new Date(b.endDate).getTime();
                                break;
                            case 'updatedAt':
                                aVal = new Date(a.updatedAt).getTime();
                                bVal = new Date(b.updatedAt).getTime();
                                break;
                            case 'status':
                                aVal = a.status;
                                bVal = b.status;
                                break;
                            default:
                                aVal = a.title.toLowerCase();
                                bVal = b.title.toLowerCase();
                        }

                        if (sortOrder === 'asc') {
                            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                        } else {
                            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
                        }
                    });

                    return filtered;
                },

                getActiveContracts: () => {
                    return get().contracts.filter(c => c.status === 'active');
                },

                getPendingSignatureContracts: () => {
                    return get().contracts.filter(c =>
                        ['sent', 'negotiation', 'signed'].includes(c.status)
                    );
                },

                getSignedContracts: () => {
                    return get().contracts.filter(c =>
                        ['signed', 'active', 'completed'].includes(c.status)
                    );
                },

                getContractsByStatus: () => {
                    const contracts = get().contracts;
                    return contracts.reduce((acc, contract) => {
                        const status = contract.status;
                        if (!acc[status]) {
                            acc[status] = [];
                        }
                        acc[status].push(contract);
                        return acc;
                    }, {} as Record<string, Contract[]>);
                },

                getTotalValue: () => {
                    return get().contracts.reduce((sum, c) => sum + c.totalValue, 0);
                },

                getCompletedValue: () => {
                    return get().contracts
                        .filter(c => c.status === 'completed')
                        .reduce((sum, c) => sum + c.totalValue, 0);
                },
            }),
            {
                name: 'contracts-storage',
                partialize: (state) => ({
                    contracts: state.contracts,
                    templates: state.templates,
                    searchTerm: state.searchTerm,
                    filterStatus: state.filterStatus,
                    filterTab: state.filterTab,
                    sortBy: state.sortBy,
                    sortOrder: state.sortOrder,
                }),
            }
        ),
        {
            name: 'contracts-store',
        }
    )
);
